import { useState, useRef, FormEvent, ChangeEvent, KeyboardEvent } from 'react';
import {
  PaperAirplaneIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { useChatStore } from '../../stores/chatStore';
import { imageApi } from '../../services/api';

export default function ChatInput() {
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { sendMessage, isSending } = useChatStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError(null);
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !imageFile) return;
    if (isSending || isUploading) return;

    setError(null);
    
    try {
      let imageUrl: string | undefined;
      
      // Upload image if present
      if (imageFile) {
        setIsUploading(true);
        imageUrl = await imageApi.upload(imageFile);
        setIsUploading(false);
      }

      // Send message
      await sendMessage(trimmedMessage || 'What is this?', imageUrl);
      
      // Clear input
      setMessage('');
      removeImage();
      
      // Focus textarea
      textareaRef.current?.focus();
    } catch (err) {
      setIsUploading(false);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isDisabled = isSending || isUploading;
  const canSubmit = (message.trim() || imageFile) && !isDisabled;

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <div className="max-w-3xl mx-auto">
        {/* Error message */}
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Image preview */}
        {imagePreview && (
          <div className="mb-3 relative inline-block">
            <img
              src={imagePreview}
              alt="Selected image"
              className="max-h-32 rounded-lg border border-gray-200"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 p-1 bg-gray-800 rounded-full text-white hover:bg-gray-700 transition-colors"
              aria-label="Remove image"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Input form */}
        <form onSubmit={handleSubmit} className="flex items-end gap-3">
          {/* Image upload button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isDisabled}
            className="p-3 text-gray-500 hover:text-wise hover:bg-gray-100 rounded-full transition-colors btn-touch disabled:opacity-50"
            aria-label="Upload image"
          >
            <PhotoIcon className="h-6 w-6" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            aria-hidden="true"
          />

          {/* Text input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your question here..."
              disabled={isDisabled}
              rows={1}
              className="w-full resize-none rounded-2xl border border-gray-300 px-4 py-3 pr-12 text-base focus:border-wise focus:ring-2 focus:ring-wise/20 outline-none disabled:bg-gray-50 disabled:opacity-50 input-touch"
              style={{ minHeight: '48px', maxHeight: '120px' }}
              aria-label="Message input"
            />
          </div>

          {/* Send button */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="p-3 bg-wise text-white rounded-full hover:bg-wise-dark transition-colors btn-touch disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            {isUploading ? (
              <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <PaperAirplaneIcon className="h-6 w-6" />
            )}
          </button>
        </form>

        {/* Helper text */}
        <p className="mt-2 text-xs text-gray-400 text-center">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
